import { useCallback, useMemo, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { MenuItem, Typography } from "@mui/material"
import { useFormikContext } from "formik"
import { isEmpty } from "lodash"
import { MRT_ColumnDef, MRT_Row } from "material-react-table"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus } from "@fortawesome/pro-solid-svg-icons"

import { ApiQueryTable } from "../../../../../../../core/api/components/ApiQueryTable"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import { StandardForm } from "../../../../../../../shared/components/forms/StandardForm"
import { StandardMainPanel } from "../../../../../../../shared/components/forms/StandardMainPanel"

import { CreateOrEditPriceListFormValues, CustomerFlat } from "../../../priceListTypes"
import { convertCustomersByUserGroup, convertFlatToCustomers } from "../../../helpers"
import { AddCustomerDialog } from "./AddCustomerDialog"
import { DestructiveMenuItem } from "../../../../../../../shared/components/menus/DestructiveMenuItem"

const useStyles = makeStyles()((theme) => ({
    listTitle: {
        color: theme.palette.tailwind.slate[900],
        background: theme.palette.tailwind.slate[50],
        padding: ".75rem",
        display: "block",
    },
    listEmpty: {
        fontStyle: "italic",
        color: theme.palette.tailwind.slate[400],
        padding: "1.25rem .75rem",
        borderBottom: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    tablePaper: {
        boxShadow: "none",
    },
    tableContainer: {
        maxHeight: "100%",
        borderRadius: 0,
        padding: "0",
    },
    tableHeadCell: {
        border: "none",
        color: theme.palette.tailwind.slate[500],
    },
    tableHeadRow: {
        boxShadow: "none",
        background: theme.palette.tailwind.slate[50],
    },
}))

export function FormCustomerContent() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { setFieldValue, values } = useFormikContext<CreateOrEditPriceListFormValues>()
    const [showAddCustomerDialog, setShowAddCustomerDialog] = useState(false)

    const customersFlat = useMemo(() => {
        return convertCustomersByUserGroup(values.customers)
    }, [values.customers])

    const columns = useMemo<MRT_ColumnDef<CustomerFlat>[]>(
        () => [
            {
                header: l10n.getString("create-or-edit-price-list-customer-table-header-name"),
                accessorKey: "customerName",
                size: 100,
            },
            {
                header: l10n.getString("create-or-edit-price-list-customer-table-header-user-group"),
                accessorKey: "userGroupName",
                minSize: 200,
                maxSize: 1000,
            },
        ],
        [l10n],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<CustomerFlat>; closeMenu: () => void }) => [
            <DestructiveMenuItem
                key="remove"
                onClick={() => {
                    const newCustomersFlat = customersFlat.filter(
                        (item) => item.userGroupId !== row.original.userGroupId,
                    )
                    const newCustomers = convertFlatToCustomers(newCustomersFlat)
                    setFieldValue("customers", newCustomers)
                    closeMenu()
                }}
            >
                <LocalizedStrict id="create-or-edit-price-list-customer-table-button-remove">
                    <span>Remove</span>
                </LocalizedStrict>
            </DestructiveMenuItem>,
            // <MenuItem
            //     key="details"
            //     onClick={() => {
            //         closeMenu()
            //     }}
            // >
            //     <LocalizedStrict id="create-or-edit-price-list-customer-table-button-details">
            //         <span>Details</span>
            //     </LocalizedStrict>
            // </MenuItem>,
        ],
        [customersFlat, setFieldValue],
    )

    return (
        <>
            <StandardForm>
                <StandardMainPanel>
                    <LocalizedStrict id="create-or-edit-price-list-customer-subtitle">
                        <Typography variant="body2">
                            If no customers are added to the access list, then all customers have access.
                        </Typography>
                    </LocalizedStrict>

                    <div>
                        <LocalizedStrict id="create-or-edit-price-list-customer-table-title">
                            <Typography variant="subtitle1" className={classes.listTitle}>
                                Customers with access
                            </Typography>
                        </LocalizedStrict>

                        {isEmpty(values.customers) ? (
                            <LocalizedStrict id="create-or-edit-price-list-customer-table-empty-state">
                                <Typography variant="body2" className={classes.listEmpty}>
                                    All customers have access
                                </Typography>
                            </LocalizedStrict>
                        ) : (
                            <ApiQueryTable
                                isLoading={false}
                                isFetching={false}
                                isError={false}
                                data={customersFlat}
                                columns={columns}
                                enableClickToCopy={false}
                                enableColumnOrdering
                                enableStickyHeader
                                enableDensityToggle={false}
                                initialState={{ density: "spacious" }}
                                enableHiding={false}
                                enableRowActions
                                enableBottomToolbar={false}
                                enableTopToolbar={false}
                                muiTablePaperProps={{ className: classes.tablePaper }}
                                muiTableContainerProps={{ className: classes.tableContainer }}
                                muiTableHeadCellProps={{ className: classes.tableHeadCell }}
                                muiTableHeadRowProps={{ className: classes.tableHeadRow }}
                                positionActionsColumn="last"
                                enableColumnDragging={false}
                                renderRowActionMenuItems={renderRowActionMenuItems}
                            />
                        )}
                    </div>

                    <div>
                        <LocalizedStrict id="create-or-edit-price-list-customer-button-add-customer">
                            <StandardButton
                                onClick={() => setShowAddCustomerDialog(true)}
                                color="blue"
                                fullWidth={false}
                                startIcon={<FontAwesomeIcon icon={faPlus} />}
                            >
                                Add customers
                            </StandardButton>
                        </LocalizedStrict>
                    </div>
                </StandardMainPanel>
            </StandardForm>

            {showAddCustomerDialog && (
                <AddCustomerDialog open={showAddCustomerDialog} onClose={() => setShowAddCustomerDialog(false)} />
            )}
        </>
    )
}
